import React, { useState } from "react";
import { AlertTriangle, Clock, Globe, Loader2, Repeat, Users, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  WEEKDAYS, allTimeZones, describeEnd, describeRecurrence, formatDateOnly, formatInZone, sameZone,
  zoneLabel,
} from "../../lib/scheduleTime";
import { jobIssue, jobOutcome, seriesTotals } from "../../lib/pushOutcome";

const DIALOG_FIT =
  "w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto";
const INPUT = "block w-full max-w-full border border-[hsl(var(--kb-border))] p-2 text-sm bg-white min-h-[44px]";

export const EMPTY_RECURRENCE = {
  frequency: "weekly", weekdays: [], time: "10:00", start_date: "",
  end_type: "never", until_date: "", max_occurrences: "10",
};

/** Body fields the API expects for a recurrence (form strings → API types). */
export function recurrencePayload(r) {
  return {
    frequency: r.frequency,
    weekdays: r.frequency === "weekly" ? [...r.weekdays].sort((a, b) => a - b) : [],
    time: r.time,
    start_date: r.start_date,
    end_type: r.end_type,
    until_date: r.end_type === "until" ? r.until_date : null,
    max_occurrences: r.end_type === "count" ? Number(r.max_occurrences) : null,
  };
}

/**
 * The store's time zone, shown wherever a time is entered. Every schedule on
 * this store is entered and displayed in it. "Change" saves it for the store.
 */
export function StoreTimeZone({ timeZone, deviceTimeZone, onChange, saving }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="text-xs border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-2" data-testid="push-store-tz">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Globe size={12} className="shrink-0" />
        <span>Store time zone: <b data-testid="push-store-tz-value">{zoneLabel(timeZone)}</b></span>
        {saving && <Loader2 size={12} className="animate-spin" />}
        <button type="button" className="underline min-h-[32px] px-1" onClick={() => setEditing((v) => !v)}
          data-testid="push-store-tz-change">{editing ? "Done" : "Change"}</button>
      </div>
      {editing && (
        <select className={`${INPUT} mt-2`} value={timeZone} disabled={saving} aria-label="Store time zone"
          onChange={(e) => onChange(e.target.value)} data-testid="push-store-tz-select">
          {[...new Set([timeZone, ...allTimeZones()])].map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      )}
      {deviceTimeZone && !sameZone(deviceTimeZone, timeZone) && (
        <div className="text-amber-700 mt-1" data-testid="push-store-tz-differs">
          This device is on {deviceTimeZone}. Times below are in the store's time zone, not this device's.
        </div>
      )}
    </div>
  );
}

/** Daily / weekly rule, start date and optional end. */
export function RecurrencePicker({ value, onChange, today, maxStart, limits, error, preview, timeZone }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const toggleDay = (d) => set({
    weekdays: value.weekdays.includes(d) ? value.weekdays.filter((x) => x !== d) : [...value.weekdays, d],
  });
  const maxTimes = limits?.max_occurrences || 365;
  return (
    <div className="mt-3 space-y-3" data-testid="push-recurrence">
      <div>
        <label className="text-sm font-medium" htmlFor="push-rec-frequency">Repeats</label>
        <select id="push-rec-frequency" className={`${INPUT} mt-1`} value={value.frequency}
          onChange={(e) => set({ frequency: e.target.value })} data-testid="push-rec-frequency">
          <option value="daily">Every day</option>
          <option value="weekly">Every week on…</option>
        </select>
      </div>

      {value.frequency === "weekly" && (
        <div>
          <div className="text-sm font-medium" id="push-rec-days-label">Days</div>
          <div className="flex flex-wrap gap-2 mt-1" role="group" aria-labelledby="push-rec-days-label" data-testid="push-rec-days">
            {WEEKDAYS.map((d) => {
              const on = value.weekdays.includes(d.key);
              return (
                <button key={d.key} type="button" aria-pressed={on} aria-label={d.long} onClick={() => toggleDay(d.key)}
                  className={`border min-w-[44px] min-h-[44px] px-2 text-sm ${on ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))] text-white" : "border-[hsl(var(--kb-border))] bg-white"}`}
                  data-testid={`push-rec-day-${d.key}`}>
                  {d.short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium" htmlFor="push-rec-time">Time</label>
          <input id="push-rec-time" type="time" className={`${INPUT} mt-1`} value={value.time}
            onChange={(e) => set({ time: e.target.value })} data-testid="push-rec-time" />
        </div>
        <div>
          <label className="text-sm font-medium" htmlFor="push-rec-start">Starts on</label>
          <input id="push-rec-start" type="date" className={`${INPUT} mt-1`} value={value.start_date}
            min={today} max={maxStart} onChange={(e) => set({ start_date: e.target.value })} data-testid="push-rec-start" />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium" htmlFor="push-rec-end">Ends</label>
        <select id="push-rec-end" className={`${INPUT} mt-1`} value={value.end_type}
          onChange={(e) => set({ end_type: e.target.value })} data-testid="push-rec-end">
          <option value="never">Never (until I cancel it)</option>
          <option value="until">On a date</option>
          <option value="count">After a number of times</option>
        </select>
        {value.end_type === "until" && (
          <input type="date" aria-label="End date" className={`${INPUT} mt-2`} value={value.until_date}
            min={value.start_date || today} onChange={(e) => set({ until_date: e.target.value })} data-testid="push-rec-until" />
        )}
        {value.end_type === "count" && (
          <div className="flex items-center gap-2 mt-2">
            <input type="number" min={1} max={maxTimes} step={1} inputMode="numeric" aria-label="Number of times"
              className={`${INPUT} !w-28`} value={value.max_occurrences}
              onChange={(e) => set({ max_occurrences: e.target.value })} data-testid="push-rec-count" />
            <span className="text-sm">times</span>
          </div>
        )}
      </div>

      {error && <div className="text-xs text-red-600" role="alert" data-testid="push-rec-error">{error}</div>}
      {!error && preview && (
        <div className="text-xs" data-testid="push-rec-preview">
          <b>{describeRecurrence(value)}</b> · first send <b>{formatInZone(preview.first_run_at, timeZone)}</b>
        </div>
      )}
    </div>
  );
}

const SERIES_STATUS = {
  active: ["Active", "bg-green-50 text-green-800 border-green-200"],
  cancelled: ["Cancelled", "bg-neutral-100 text-neutral-600 border-neutral-200"],
  completed: ["Completed", "bg-blue-50 text-blue-800 border-blue-200"],
};

function SeriesItem({ series, audienceLabel, onAskCancel, cancelling }) {
  const [label, cls] = SERIES_STATUS[series.status] || [series.status, "bg-neutral-100 border-neutral-200"];
  return (
    <li className="border border-[hsl(var(--kb-border))] p-3 text-sm" data-testid={`recurring-item-${series.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium min-w-0 break-words [overflow-wrap:anywhere]">{series.title}</div>
        <span className={`inline-flex items-center border px-2 py-0.5 text-[11px] font-medium shrink-0 ${cls}`}
          data-testid={`recurring-status-${series.id}`} data-status={series.status}>{label}</span>
      </div>
      <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5 line-clamp-2 break-words [overflow-wrap:anywhere]">{series.body}</div>
      <div className="flex items-start gap-1.5 mt-2 text-xs" data-testid={`recurring-rule-${series.id}`}>
        <Repeat size={12} className="mt-0.5 shrink-0" />
        <span className="min-w-0">
          <b>{describeRecurrence(series)}</b>
          <span className="text-[hsl(var(--kb-muted))]"> · {series.timezone}</span>
        </span>
      </div>
      <div className="flex items-start gap-1.5 mt-1 text-xs" data-testid={`recurring-next-${series.id}`}>
        <Clock size={12} className="mt-0.5 shrink-0" />
        <span className="min-w-0">
          {series.status === "active" && series.next_run_at
            ? <>Next: {formatInZone(series.next_run_at, series.timezone)}</>
            : series.status === "cancelled" ? "No further sends" : "Finished"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[hsl(var(--kb-muted))]">
        <span className="inline-flex items-center gap-1.5 min-w-0"><Users size={12} className="shrink-0" /> <span className="truncate">{audienceLabel(series)}</span></span>
        <span data-testid={`recurring-ends-${series.id}`}>
          From {formatDateOnly(series.start_date)} · {describeEnd(series)}
        </span>
      </div>
      <LastRun series={series} />
      {series.status === "active" && (
        <button type="button" onClick={() => onAskCancel(series)} disabled={cancelling}
          className="kb-btn-outline mt-3 inline-flex items-center justify-center gap-1.5 text-xs !py-2 w-full sm:w-auto min-h-[40px]"
          data-testid={`recurring-cancel-${series.id}`}>
          {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
          {cancelling ? "Cancelling…" : "Cancel recurring"}
        </button>
      )}
    </li>
  );
}

/** Runs so far, people reached, and what happened on the last run — "N sent"
 *  used to count runs, so a run that reached nobody still read "1 sent". */
function LastRun({ series }) {
  const run = series.last_run;
  const outcome = jobOutcome(run);
  const issue = jobIssue(run);
  return (
    <div className="mt-2 text-xs space-y-1" data-testid={`recurring-runs-${series.id}`}>
      <div className="text-[hsl(var(--kb-muted))]" data-testid={`recurring-totals-${series.id}`}>{seriesTotals(series)}</div>
      {run && (
        <div className="min-w-0 break-words [overflow-wrap:anywhere]" data-testid={`recurring-last-run-${series.id}`}>
          <span className="font-medium">Last run{run.scheduled_for ? ` (${formatInZone(run.scheduled_for, series.timezone)})` : ""}:</span>{" "}
          <span data-status={run.status} data-testid={`recurring-last-run-status-${series.id}`}>
            {{ sent: "Sent", partial: "Partly sent", failed: "Failed", skipped: "Not sent", cancelled: "Cancelled" }[run.status] || run.status}
          </span>
          {outcome && <> — {outcome}</>}
        </div>
      )}
      {issue && (
        <div className="text-red-700 min-w-0 break-words [overflow-wrap:anywhere]" data-testid={`recurring-issue-${series.id}`}>{issue}</div>
      )}
      {series.last_error && (
        <div className="flex items-start gap-1.5 text-red-700 min-w-0 break-words [overflow-wrap:anywhere]" data-testid={`recurring-error-${series.id}`}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>This schedule could not run: {series.last_error}. It is retried automatically; contact support if it persists.</span>
        </div>
      )}
    </div>
  );
}

/** This store's recurring schedules, with a confirmed Cancel. */
export function RecurringList({ items, audienceLabel, onCancel, cancellingId }) {
  const [confirming, setConfirming] = useState(null);
  const active = items.filter((s) => s.status === "active");
  const ended = items.filter((s) => s.status !== "active");
  return (
    <div data-testid="recurring-list">
      <h3 className="text-xs uppercase tracking-wide text-[hsl(var(--kb-muted))] mb-2">Recurring ({active.length} active)</h3>
      {items.length === 0 ? (
        <div className="text-sm text-[hsl(var(--kb-muted))]">No recurring notifications.</div>
      ) : (
        <ul className="space-y-3">
          {[...active, ...ended].map((s) => (
            <SeriesItem key={s.id} series={s} audienceLabel={audienceLabel}
              onAskCancel={setConfirming} cancelling={cancellingId === s.id} />
          ))}
        </ul>
      )}

      <AlertDialog open={!!confirming} onOpenChange={(v) => !v && setConfirming(null)}>
        <AlertDialogContent className={`max-w-md ${DIALOG_FIT}`} data-testid="recurring-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this recurring notification?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirming?.title}” will stop: no further notifications will be sent. Ones already sent are not affected. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="w-full sm:w-auto mt-0" data-testid="recurring-cancel-keep">Keep it</AlertDialogCancel>
            <AlertDialogAction className="w-full sm:w-auto" data-testid="recurring-cancel-confirm"
              onClick={() => { const s = confirming; setConfirming(null); onCancel(s); }}>
              Cancel recurring
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
